/**
 * Adapter utilities for bridging KnockoutJS observables ↔ plain tiebreaker types.
 *
 * Usage pattern in App classes:
 *   1. Call buildGameResultsLookup(this.Weeks, maxWeek) to get a GameResultsLookup
 *   2. Call toNhlTiebreakerTeam(teamObs) (etc.) for each team
 *   3. Call the extracted tiebreaker service (nhlConferenceRanker, etc.)
 *   4. Call applyResult(result, teamObsMap) to write back to KO observables
 */
import type {
  GameResult, GameResultsLookup,
  TiebreakerTeam, TiebreakerResult,
  NhlTiebreakerTeam, NflTiebreakerTeam,
  MlbTiebreakerTeam, NbaTiebreakerTeam,
  GameRecord,
} from '../types';
import type { ITeam } from '../../interfaces/ITeam';
import type { IGameRecord } from '../../interfaces/IGameRecord';
import { SelectionType } from '../../enums/selection-type';
import { RankMethod } from '../../enums/rank-method';
import { ArrayGameResultsLookup } from '../game-results-lookup';
import { calcPct, calcPctNaN } from '../helpers';

// ─── Game extraction ────────────────────────────────────────────────────────

/**
 * Extract GameResult[] from KO Weeks structure.
 * Iterates all regular-season weeks and their games.
 */
export function extractGamesFromWeeks(
  weeks: any[],
  maxWeek: number,
  slotIndices: number[],
): GameResult[] {
  const results: GameResult[] = [];

  for (let w = 1; w <= maxWeek; w++) {
    const weekSlots = weeks[w];
    if (!weekSlots) continue;

    for (const slotIdx of slotIndices) {
      const slot = weekSlots[slotIdx];
      if (!slot) continue;

      const gamesComputed = slot.GamesComputed?.();
      if (!gamesComputed) continue;

      for (const gameObs of gamesComputed) {
        const game = gameObs();
        if (!game || !game.selectionMade?.()) continue;

        const home = game.home?.();
        const away = game.away?.();
        if (!home || !away) continue;

        const winner = game.TeamThatWon?.()?.()?.TeamAbbreviation ?? null;
        const selType = game.selectionType?.() ?? SelectionType.NONE;
        // SortOrder === 1 is the sentinel for "no date" — treat as undefined
        const rawSortOrder: number | undefined = game.SortOrder?.();
        const sortOrder = rawSortOrder != null && rawSortOrder !== 1 ? rawSortOrder : undefined;

        results.push({
          homeTeam: home.TeamAbbreviation,
          awayTeam: away.TeamAbbreviation,
          winner,
          selectionType: selType,
          sortOrder,
        });
      }
    }
  }

  return results;
}

/**
 * Build a GameResultsLookup from the Weeks KO structure.
 */
export function buildGameResultsLookup(
  weeks: any[],
  maxWeek: number,
  slotIndices: number[],
): GameResultsLookup {
  return new ArrayGameResultsLookup(extractGamesFromWeeks(weeks, maxWeek, slotIndices));
}

// ─── IGameRecord → GameRecord conversion ────────────────────────────────────

function toGameRecord(rec: IGameRecord): GameRecord {
  const w = rec.Wins();
  const l = rec.Losses();
  const t = rec.Ties();
  return {
    wins: w,
    losses: l,
    ties: t,
    pct: calcPct([w, l, t]),
    pctNaN: calcPctNaN([w, l, t]),
  };
}

// ─── Team conversion: KO observable → plain tiebreaker type ─────────────────

export function toNhlTiebreakerTeam(teamObs: KnockoutObservable<ITeam>): NhlTiebreakerTeam {
  const t = teamObs();
  const gr = t.GameRecords as any; // NhlGameRecords
  return {
    teamAbbreviation: t.TeamAbbreviation,
    teamId: t.TeamID,
    teamName: t.TeamName,
    division: t.Division,
    conference: t.Conference,
    records: {
      all: toGameRecord(t.GameRecords.AllRecord),
      division: toGameRecord(t.GameRecords.DivisionRecord),
      conference: toGameRecord(t.GameRecords.ConferenceRecord),
    },
    points: gr.Points?.() ?? 0,
    pointsPct: gr.PointsPct?.() ?? 0,
    regulationWins: gr.Regulation?.Wins?.() ?? 0,
    row: gr.ROW?.() ?? 0,
    totalWins: t.GameRecords.AllRecord.Wins(),
    // Goal data is not tracked in IGame; default to 0 (no-break sentinel in goal-differential step)
    goalsFor: 0,
    goalsAgainst: 0,
  };
}

export function toNflTiebreakerTeam(teamObs: KnockoutObservable<ITeam>): NflTiebreakerTeam {
  const t = teamObs();
  return {
    teamAbbreviation: t.TeamAbbreviation,
    teamId: t.TeamID,
    teamName: t.TeamName,
    division: t.Division,
    conference: t.Conference,
    records: {
      all: toGameRecord(t.GameRecords.AllRecord),
      division: toGameRecord(t.GameRecords.DivisionRecord),
      conference: toGameRecord(t.GameRecords.ConferenceRecord),
    },
    sov: t.SOV(),
    sos: t.SOS(),
  };
}

export function toMlbTiebreakerTeam(
  teamObs: KnockoutObservable<ITeam>,
  isDivisionWinner: boolean,
): MlbTiebreakerTeam {
  const t = teamObs();
  return {
    teamAbbreviation: t.TeamAbbreviation,
    teamId: t.TeamID,
    teamName: t.TeamName,
    division: t.Division,
    conference: t.Conference,
    records: {
      all: toGameRecord(t.GameRecords.AllRecord),
      division: toGameRecord(t.GameRecords.DivisionRecord),
      conference: toGameRecord(t.GameRecords.ConferenceRecord),
    },
    isDivisionWinner,
  };
}

export function toNbaTiebreakerTeam(
  teamObs: KnockoutObservable<ITeam>,
  isDivisionWinner: boolean,
): NbaTiebreakerTeam {
  const t = teamObs();
  return {
    teamAbbreviation: t.TeamAbbreviation,
    teamId: t.TeamID,
    teamName: t.TeamName,
    division: t.Division,
    conference: t.Conference,
    records: {
      all: toGameRecord(t.GameRecords.AllRecord),
      division: toGameRecord(t.GameRecords.DivisionRecord),
      conference: toGameRecord(t.GameRecords.ConferenceRecord),
    },
    isDivisionWinner,
  };
}

// ─── Result application: write tiebreaker results back to KO observables ────

/**
 * Apply tiebreaker results back to KO team observables.
 * Sets RankMethod and Tiebreakers.Explanations on each team.
 */
export function applyTiebreakerResult<T extends TiebreakerTeam>(
  result: TiebreakerResult<T>,
  teamObsMap: Map<string, KnockoutObservable<ITeam>>,
  options?: { preventRankOverride?: boolean },
): void {
  if (!options?.preventRankOverride) {
    for (const [abbr, rankMethod] of result.rankMethods) {
      const teamObs = teamObsMap.get(abbr);
      if (teamObs) {
        teamObs().RankMethod(rankMethod);
      }
    }
  }

  for (const [abbr, explanations] of result.explanations) {
    const teamObs = teamObsMap.get(abbr);
    if (teamObs) {
      const tiebreakers = teamObs().Tiebreakers();
      if (tiebreakers) {
        tiebreakers.Explanations = explanations;
        teamObs().Tiebreakers.valueHasMutated();
      }
    }
  }

  for (const [abbr, details] of result.explainerDetails ?? []) {
    const teamObs = teamObsMap.get(abbr);
    if (teamObs) {
      const tiebreakers = teamObs().Tiebreakers();
      if (tiebreakers) {
        if (details.HTH) {
          tiebreakers.HTH = details.HTH;
        }
        if (details.DivHTH) {
          tiebreakers.DivHTH = details.DivHTH;
        }
        if (details.CommonGames) {
          tiebreakers.CommonGames = details.CommonGames;
        }
        teamObs().Tiebreakers.valueHasMutated();
      }
    }
  }
}

/**
 * Build a team abbreviation → KO observable map for result application.
 */
export function buildTeamObsMap(
  teamObservables: KnockoutObservable<ITeam>[],
): Map<string, KnockoutObservable<ITeam>> {
  const map = new Map<string, KnockoutObservable<ITeam>>();
  for (const obs of teamObservables) {
    map.set(obs().TeamAbbreviation, obs);
  }
  return map;
}

/**
 * Reorder KO team observables to match the tiebreaker result ordering.
 * Returns the team observables in the same order as result.orderedTeams.
 */
export function reorderTeamObs<T extends TiebreakerTeam>(
  result: TiebreakerResult<T>,
  teamObsMap: Map<string, KnockoutObservable<ITeam>>,
): KnockoutObservable<ITeam>[] {
  return result.orderedTeams
    .map(t => teamObsMap.get(t.teamAbbreviation))
    .filter((obs): obs is KnockoutObservable<ITeam> => obs != null);
}
